import { useState } from "react";
import { Check, Award, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { resolveIcon } from "../shared/iconMap";
import PriceGate from "../shared/PriceGate";
import { AirlineSlideHead } from "../shared/AirlineSlideRenderers";

const fmt = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SlideRBACommercials = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const brand = airline.brandColor || "#FFCD00";
  const c = airline?.content?.commercials || {};
  const options = c.options || [];
  const keyStrengths = c.keyStrengths || [];
  const termsAndConditions = c.termsAndConditions || [];
  const [activeOption, setActiveOption] = useState(options[0]?.id || null);
  const [showStrengths, setShowStrengths] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <PriceGate accentColor={brand} isDark={isDark}>
      {({ revealed, Toggle, lockedNode }) => (
        <div>
          <AirlineSlideHead
            eyebrow="Commercials"
            title="Three options,"
            accentLine="one team."
            airline={airline}
            accent={brand}
            action={Toggle}
          />
          <p className={`k-lead max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
            {c.subheading}
          </p>

          {!revealed ? (
            <div className="mt-10">{lockedNode}</div>
          ) : (
            <>
              <div className={`mt-12 pt-8 border-t grid grid-cols-3 gap-6 ${isDark ? "border-white/10" : "border-black/10"}`}>
                {options.map((opt) => {
                  const total = Number(opt.licensing) + Number(opt.technical);
                  const isActive = activeOption === opt.id;
                  const BadgeIcon = opt.badgeIconName ? resolveIcon(opt.badgeIconName) : null;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setActiveOption(opt.id)}
                      data-testid={`option-${opt.id}`}
                      className={`text-left rounded-2xl p-6 border transition-all flex flex-col ${
                        isDark
                          ? isActive ? "bg-white/[0.06]" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
                          : isActive ? "bg-white" : "bg-white border-black/10 hover:border-black/25"
                      }`}
                      style={isActive ? { borderColor: brand, boxShadow: `0 12px 40px -20px ${brand}` } : undefined}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className={`text-[22px] font-semibold tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{opt.label}</div>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wider flex-shrink-0"
                          style={{ backgroundColor: `${brand}25`, color: isDark ? brand : "#8B6600" }}
                        >
                          {BadgeIcon && <BadgeIcon className="w-3 h-3" />}
                          {opt.badge}
                        </span>
                      </div>
                      <div className="text-[13px] font-semibold mb-4" style={{ color: brand }}>{opt.positioning}</div>
                      <div className={`space-y-1.5 text-[13px] mb-4 ${isDark ? "text-white/70" : "text-black/65"}`}>
                        <div className="flex justify-between"><span>Annual licensing</span><span className="font-semibold">{fmt(opt.licensing)}</span></div>
                        <div className="flex justify-between"><span>Annual technical</span><span className="font-semibold">{fmt(opt.technical)}</span></div>
                      </div>
                      <div className={`rounded-xl px-4 py-3 mb-3 flex items-center justify-between ${isDark ? "bg-[#FFCD00]/12" : "bg-[#FFCD00]/15"}`}>
                        <div className="k-eyebrow" style={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }}>Total / year</div>
                        <div className="text-[22px] font-semibold tracking-tight" style={{ color: isDark ? "#FFCD00" : "#8B6600" }}>{fmt(total)}</div>
                      </div>
                      <p className={`text-[12px] leading-snug italic mt-auto ${isDark ? "text-white/50" : "text-black/50"}`}>{opt.note}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 grid grid-cols-2 gap-6">
                <Collapse
                  isDark={isDark} brand={brand}
                  open={showStrengths} onToggle={() => setShowStrengths((v) => !v)}
                  testid="toggle-strengths"
                  eyebrow={<><Award className="w-4 h-4" style={{ color: brand }} /> {keyStrengths.length} Key strengths</>}
                >
                  <ul className="space-y-2">
                    {keyStrengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] leading-snug">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: brand }} />
                        <span className={isDark ? "text-white/75" : "text-black/70"}>{s}</span>
                      </li>
                    ))}
                  </ul>
                </Collapse>
                <Collapse
                  isDark={isDark} brand={brand}
                  open={showTerms} onToggle={() => setShowTerms((v) => !v)}
                  testid="toggle-terms"
                  eyebrow={<span>Summary of terms & conditions</span>}
                >
                  <dl className="space-y-1.5 text-[13px]">
                    {termsAndConditions.map((t, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <dt className={`font-semibold min-w-[120px] ${isDark ? "text-white/70" : "text-black/70"}`}>{t.label}</dt>
                        <dd className={isDark ? "text-white/55" : "text-black/55"}>{t.value}</dd>
                      </div>
                    ))}
                  </dl>
                </Collapse>
              </div>
            </>
          )}
        </div>
      )}
    </PriceGate>
  );
};

const Collapse = ({ isDark, open, onToggle, testid, eyebrow, children }) => (
  <div className={`rounded-2xl border ${isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"}`}>
    <button
      type="button" onClick={onToggle} data-testid={testid}
      className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left"
    >
      <div className={`flex items-center gap-2 text-[14px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        {eyebrow}
      </div>
      <ChevronDown
        className={`w-4 h-4 transition-transform ${isDark ? "text-white/45" : "text-black/45"}`}
        style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
      />
    </button>
    {open && (
      <div className={`px-5 pb-5 border-t pt-4 ${isDark ? "border-white/10" : "border-black/10"}`}>
        {children}
      </div>
    )}
  </div>
);

export default SlideRBACommercials;
